import { ArrowLeft } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import SquiggleAccent from './SquiggleAccent'
import type { ReactNode } from 'react'

type BackLink = {
  label: string
  to: '/blog' | '/blog/tag' | '/playground'
}

export default function EditorialHero({
  eyebrow,
  title,
  accent,
  description,
  meta,
  back,
}: {
  eyebrow: string
  title: string
  accent?: string
  description?: ReactNode
  meta?: ReactNode
  back?: BackLink
}) {
  return (
    <section className="relative overflow-hidden bg-forest-strong pb-16 pt-28 text-cream-50 sm:pb-24 sm:pt-36">
      <div className="preview-blueprint absolute inset-0" aria-hidden="true" />
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_72%_12%,rgba(172,196,173,0.16),transparent_34rem)]"
        aria-hidden="true"
      />
      <span
        className="preview-cross left-6 top-28 hidden sm:block"
        aria-hidden="true"
      />
      <span
        className="preview-cross right-6 top-28 hidden sm:block"
        aria-hidden="true"
      />

      <div className="container-custom relative">
        {back && (
          <Link
            to={back.to}
            className="preview-link-sweep preview-mono focus-ring mb-10 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50/85 transition-colors hover:text-sage-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {back.label}
          </Link>
        )}
        <p className="preview-mono flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.24em] text-sage-200">
          <span>{eyebrow}</span>
          <span>+</span>
        </p>
        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-end">
          <div>
            <h1 className="max-w-5xl break-words font-heading text-[clamp(2.8rem,9vw,7.6rem)] leading-[0.88] tracking-[-0.045em] [overflow-wrap:anywhere]">
              {title}
              {accent && (
                <span className="block">
                  <SquiggleAccent className="text-sage-200">
                    {accent}
                  </SquiggleAccent>
                </span>
              )}
            </h1>
            {description && (
              <div className="mt-7 max-w-2xl text-sm leading-7 text-cream-50/85 sm:text-base sm:leading-8">
                {description}
              </div>
            )}
          </div>
          {meta && (
            <div className="border-t border-white/15 pt-5 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
              {meta}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
